import React, { useEffect, useRef, useState } from 'react';
import {
  Button, TextField, Container, Typography, CircularProgress, Box,
  FormControl, InputLabel, Select, MenuItem, Card, CardContent,
  InputAdornment, Checkbox, FormControlLabel, Dialog, DialogContent,
  RadioGroup, Radio, FormLabel, Divider, IconButton, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { styled } from '@mui/material/styles';
import { stripePromise } from './stripeClient';
import applepay from "../logo/apple-pay-svgrepo-com.svg";
import googlepay from "../logo/google-pay.svg";
import klarna from "../logo/klarna.svg";
import clearpay from "../logo/clearpay.svg";
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { createUserAccount, paymentFailureMessage, payWithCard } from './api';
import {
  VisaIcon,
  MastercardIcon,
  AmericanExpressIcon,
  DiscoverIcon,
  UnionpayIcon,
  JcbIcon,
} from 'react-svg-credit-card-payment-icons';
import { collection, addDoc, onSnapshot, serverTimestamp, where, query } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import StatusModal from './StatusModal';
import PaymentExpiryTimer from './PaymentExpiryTimer';



// ─── Styled components ────────────────────────────────────────────────────────

const ExtravagantContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: theme.spacing(4),
}));

const ExtravagantCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2), boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  overflow: 'hidden', position: 'relative',
  background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(8px)',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(90deg, #ffaf7b, #d76d77)',
  padding: theme.spacing(3), textAlign: 'center', color: '#fff',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3), padding: theme.spacing(1.5), fontSize: '1rem',
  background: 'linear-gradient(45deg, #d76d77, #ffaf7b)', color: '#fff',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)', transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'scale(1.02)', boxShadow: '0 6px 16px rgba(0,0,0,0.3)' },
}));

// ─── Static bank details (same account) ──────────────────────────────────────

const BANK_DETAILS = {
  bankName: 'HSBC',
  accountName: 'Tritek Consulting Limited',
  accountNumber: '32338254',
  sortCode: '40-20-23',
};

// ─── Copy row ─────────────────────────────────────────────────────────────────

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.07)',
      '&:last-of-type': { borderBottom: 'none' },
    }}>
      <Box>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.2 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 800, letterSpacing: '0.03em', fontSize: '0.97rem' }}>{value}</Typography>
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="left">
        <IconButton size="small" onClick={handleCopy} sx={{ ml: 1 }}>
          {copied
            ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'green' }} />
            : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── Bank Transfer Modal ──────────────────────────────────────────────────────

const BankTransferModal = ({ open, onClose, courseName, amount, currency }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <AccountBalanceIcon sx={{ color: '#d76d77', fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Bank Transfer Details</Typography>
      </Box>

      <Box sx={{ p: 2, mb: 2.5, borderRadius: 2, background: 'rgba(215,109,119,0.08)', border: '1.5px solid rgba(215,109,119,0.3)' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#8a1f2d', lineHeight: 1.7 }}>
          Please transfer exactly <strong>{currency === 'CAD' ? 'CAD ' : '$'}{amount}</strong> to the account below
          and use your <strong>full name</strong> as the payment reference so we can match your payment.
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, borderRadius: 2, border: '2px solid #d76d77', background: '#fff' }}>
        <CopyRow label="Bank" value={BANK_DETAILS.bankName} />
        <CopyRow label="Account Name" value={BANK_DETAILS.accountName} />
        <CopyRow label="Account Number" value={BANK_DETAILS.accountNumber} />
        <CopyRow label="Sort Code" value={BANK_DETAILS.sortCode} />
        <CopyRow label="Reference" value="Your Full Name" />
        <CopyRow label="Amount" value={`${currency === 'CAD' ? 'CAD ' : '$'}${amount}`} />
      </Box>

      <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, background: 'rgba(21,101,192,0.07)', border: '1px solid #1565c0' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1565c0', mb: 0.5 }}>What happens next?</Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
          Once we confirm receipt of your transfer, your account will be created and you will receive login details by email.
        </Typography>
      </Box>

      <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: 'rgba(46,125,50,0.06)', border: '1px solid #2e7d32' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>Course: {courseName}</Typography>
      </Box>

      <Button variant="contained" fullWidth
        sx={{ mt: 3, background: 'linear-gradient(45deg, #d76d77, #ffaf7b)', color: '#fff', fontWeight: 700 }}
        onClick={onClose}>
        I've noted the details — close
      </Button>
    </DialogContent>
  </Dialog>
);

// ─── Payment badges ───────────────────────────────────────────────────────────

const WALLET_LOGOS = [
  { label: "Apple Pay",  src: applepay,  bg: "#fff",    border: "none" },
  { label: "Google Pay", src: googlepay, bg: "#fff",    border: "1px solid #e0e0e0" },
  { label: "Klarna",     src: klarna,    bg: "#ffd900", border: "none" },
  { label: "Clearpay",   src: clearpay,  bg: "#b2fce4", border: "none" },
];

const BadgeWrap = ({ label, bg, border, children }) => (
  <Tooltip title={label} placement="top">
    <Box
      sx={{
        height: 48, px: 1.2, borderRadius: "8px",
        background: bg || "transparent",
        border: border || "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,0.13)",
        cursor: "default", userSelect: "none", overflow: "hidden",
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

const PaymentBadges = () => (
  <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
    <Typography variant="caption" sx={{ color: "#999", display: "block", textAlign: "center", mb: 1.5, fontWeight: 600 }}>
      Accepted payment methods
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, justifyContent: "center" }}>
      <BadgeWrap label="Visa" bg="#fff" border="1px solid #e8e8e8">
        <VisaIcon format="flatRounded" width={60} />
      </BadgeWrap>
      <BadgeWrap label="Mastercard" bg="#fff" border="1px solid #e8e8e8">
        <MastercardIcon format="flatRounded" width={60} />
      </BadgeWrap>
      <BadgeWrap label="American Express" bg="#fff" border="1px solid #e8e8e8">
        <AmericanExpressIcon format="flatRounded" width={60} />
      </BadgeWrap>
      <BadgeWrap label="Discover" bg="#fff" border="1px solid #e8e8e8">
        <DiscoverIcon format="flatRounded" width={60} />
      </BadgeWrap>
      <BadgeWrap label="UnionPay" bg="#fff" border="1px solid #e8e8e8">
        <UnionpayIcon format="flatRounded" width={60} />
      </BadgeWrap>
      <BadgeWrap label="JCB" bg="#fff" border="1px solid #e8e8e8">
        <JcbIcon format="flatRounded" width={60} />
      </BadgeWrap>
      {WALLET_LOGOS.map(({ label, src, bg, border }) => (
        <BadgeWrap key={label} label={label} bg={bg} border={border}>
          <img
            src={src}
            alt={label}
            height={28}
            width={64}
            style={{ objectFit: "contain", display: "block" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </BadgeWrap>
      ))}
    </Box>
  </Box>
);

// ─── Main PaymentFormUs ───────────────────────────────────────────────────────

const PaymentFormUs = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const pParam = urlParams.get('p');
  const customPrice = pParam ? parseFloat(pParam) : null;

  let courseOptions = {
    'PMBA':                 { price: 549, productId: 51922, aliases: ['PM/BA', 'PM-BA', 'PM BA'] },
    'PMDA':                 { price: 549, productId: 56409, aliases: ['PM/DA', 'PM-DA', 'PM DA'] },
    'BADA':                 { price: 549, productId: 64347, aliases: ['BA/DA', 'BA-DA', 'BA DA'] },
    "Business Analysis": { price: 459, productId: 72791, aliases: ["BA"] },
    "Project Management": { price: 459, productId: 72790, aliases: ["PM"] },
    'Cybersecurity':        { price: 399, productId: 22766 },
    'Data Analysis':        { price: 399, productId: 22767, aliases: ['Data Analytics'] },
    'Digital Marketing':    { price: 249, productId: 45867 },
    'Virtual Assistance':   { price: 199, productId: 52236, aliases: ['Virtual Assistant'] },
    'UI/UX':                { price: 199, productId: 31278, aliases: ['UI-UX', 'UI UX', 'UX/UI'] },
    'Product Management':   { price: 249, productId: 48478, aliases: ['Product Mgmt'] },
    // 'MoM':               { price: 259, productId: 29201, aliases: ['MOM'] },
    'AI for Professional':  { price: 99, productId: 52242, aliases: ['AI for Professionals', 'AI for Pros'] },
  };

  if (customPrice && Number.isFinite(customPrice) && customPrice > 0) {
    courseOptions = Object.fromEntries(
      Object.entries(courseOptions).map(([key, data]) => [key, { ...data, price: customPrice }])
    );
  }

  const courseDisplayMap = {
    PMBA: 'Project Management / Business Analysis',
    PMDA: 'Project Management / Data Analysis',
    BADA: 'Business Analysis / Data Analysis',
  };

  const normalize = (s) => (s || '').trim().toLowerCase();
  const resolveCourseFromParam = (raw) => {
    if (!raw) return null;
    const target = normalize(raw);
    for (const key of Object.keys(courseOptions)) { if (normalize(key) === target) return key; }
    for (const [key, cfg] of Object.entries(courseOptions)) {
      if (cfg.aliases?.some((a) => normalize(a) === target)) return key;
    }
    return null;
  };

  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '', phone: '', course: '' });
  const [firstPaymentAmount, setFirstPaymentAmount] = useState(549);
  const [amountError, setAmountError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('klarna');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  // Merged consent: covers Terms & Conditions + digital content access waiver
  const [acceptedConsent, setAcceptedConsent] = useState(false);

  const [paymentPlan, setPaymentPlan] = useState('part');
  const [referralCode, setReferralCode] = useState('');
  const [staff, setStaff] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [redirectOpen, setRedirectOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirmTyped, setConfirmTyped] = useState('');

  /*
    Apple Pay / Google Pay, through the Express Checkout Element.

    The Payment Request Button this replaced was removed outright in
    Stripe.js v10 — there is no paymentRequestButton element type any more,
    so the upgrade left no way to keep it. This mirrors the UK form.
  */
  const [walletMethods, setWalletMethods] = useState(null);
  const walletBoxRef = useRef(null);
  const walletElementRef = useRef(null);
  const walletElementsRef = useRef(null);
  const walletConfirmRef = useRef(null);
  const walletClickRef = useRef(null);

  const [bankModalOpen, setBankModalOpen] = useState(false);

  const getCurrencySymbol = () => paymentMethod === 'flexpay-can' ? 'CAD ' : '$';
  const activeCurrency = paymentMethod === 'flexpay-can' ? 'CAD' : 'USD';

  const selectedCourseKey = formData.course;
  const selectedCourseDisplay = courseDisplayMap[selectedCourseKey] || selectedCourseKey || '—';
  const selectedCoursePrice =
    courseOptions?.[selectedCourseKey]?.price ??
    (Number.isFinite(firstPaymentAmount) ? firstPaymentAmount : 0);

  const paymentMethodLabel =
    paymentMethod === 'klarna' ? 'Klarna'
    : paymentMethod === 'clearpay' ? 'Clearpay'
    : paymentMethod === 'paypal' ? 'PayPal'
    : paymentMethod === 'bank-transfer' ? 'Bank Transfer'
    : paymentMethod === 'flexpay-can' ? 'Card (CAD)'
    : 'Card (USD)';

  const requiresRedirectWait =
    paymentMethod === 'klarna' || paymentMethod === 'clearpay' || paymentMethod === 'paypal';

  const formIsValid =
    !!formData.email && !!formData.firstName && !!formData.lastName &&
    !!formData.phone && !!formData.course && acceptedConsent;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (!emailParam) { setStaff(null); return; }
    const staffQuery = query(collection(db, 'influencers'), where('email', '==', emailParam));
    const unsubscribe = onSnapshot(staffQuery, (snapshot) => {
      if (!snapshot.empty) { const d = snapshot.docs[0].data(); setStaff(d?.role === 'user' ? d : null); }
      else { setStaff(null); }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Survives the Klarna/Clearpay/PayPal redirect so "Go to Home" returns to this exact URL
    sessionStorage.setItem('payReturnTo', window.location.pathname + window.location.search);
    const params = new URLSearchParams(window.location.search);
    const fromParam = resolveCourseFromParam(params.get('course'));
    const chosenCourse = fromParam ?? 'PMBA';
    const courseData = courseOptions[chosenCourse];
    setFormData((prev) => ({ ...prev, course: chosenCourse }));
    setFirstPaymentAmount(courseData?.price ?? 549);
  }, []);

  useEffect(() => {
    if (!redirectOpen) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [redirectOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('referral') || params.get('ref_code');
    if (ref) setReferralCode(ref.trim().toUpperCase());
  }, []);

  useEffect(() => {
    if (!stripe || !walletBoxRef.current) return;
    let cancelled = false;
    let timer = null;

    const build = () => {
      const box = walletBoxRef.current;
      if (cancelled || !box) return;

      const els = stripe.elements({
        mode: 'payment',
        amount: Math.round((firstPaymentAmount || 199) * 100),
        currency: 'usd',
      });
      const ece = els.create('expressCheckout', {
        buttonHeight: 48,
        paymentMethods: {
          applePay: 'always', googlePay: 'always',
          link: 'never', paypal: 'never', klarna: 'never', amazonPay: 'never',
        },
      });
      walletElementsRef.current = els;
      walletElementRef.current = ece;

      const show = (avail) =>
        setWalletMethods(avail && (avail.applePay || avail.googlePay) ? avail : null);

      ece.on('ready', (e) => show(e.availablePaymentMethods));
      ece.on('availablepaymentmethodschange', (e) => show(e.availablePaymentMethods || e.paymentMethods));
      ece.on('click', (event) => walletClickRef.current && walletClickRef.current(event));
      ece.on('confirm', (event) => walletConfirmRef.current && walletConfirmRef.current(event));
      ece.mount(box);
    };

    const start = () => { timer = setTimeout(build, 400); };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('load', start);
      try { if (walletElementRef.current) walletElementRef.current.destroy(); } catch (err) { /* gone */ }
      walletElementRef.current = null;
      walletElementsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  // The sheet quotes whatever this group holds, so the chosen course has to
  // reach it.
  useEffect(() => {
    if (!walletElementsRef.current || !firstPaymentAmount) return;
    walletElementsRef.current.update({ amount: Math.round(firstPaymentAmount * 100) });
  }, [firstPaymentAmount]);

  /*
    The sheet is about to open. Refusing to resolve keeps it shut, which is
    how an incomplete form is blocked without the customer seeing a wallet
    sheet they cannot use.
  */
  const handleWalletClick = ({ resolve }) => {
    if (!formIsValid) {
      setSnackSeverity('error');
      setSnackMessage('Please complete the form and accept all consents before paying.');
      setSnackOpen(true);
      return;
    }
    resolve();
  };

  const handleWalletConfirm = async (event) => {
    const els = walletElementsRef.current;
    if (!stripe || !els) return;
    setLoading(true);
    try {
      const { error: submitError } = await els.submit();
      if (submitError) throw submitError;

      const email = event?.billingDetails?.email || formData.email;
      const name = event?.billingDetails?.name || `${formData.firstName} ${formData.lastName}`.trim();
      const phone = event?.billingDetails?.phone || formData.phone || '';

      const createPI = await fetch('https://innovators.onrender.com/stripe/create-intent', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(firstPaymentAmount * 100), currency: 'usd', method: 'card',
          email, name,
          metadata: { source: 'google-apple-pay', course: formData.course || '', courseName: selectedCourseDisplay },
          returnUrl: `${window.location.origin}/confirmation`,
        }),
      });
      const piData = await createPI.json();
      if (!createPI.ok) throw new Error(piData.error || 'Failed to create PaymentIntent');

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements: els,
        clientSecret: piData.clientSecret,
        confirmParams: { return_url: `${window.location.origin}/confirmation` },
        redirect: 'if_required',
      });
      if (confirmError) throw confirmError;

      const courseData = courseOptions[formData.course];
      await addDoc(collection(db, 'payments'), {
        status: 'succeeded', gateway: 'stripe', method: 'google-apple-pay', currency: 'USD',
        amountMinor: Math.round(firstPaymentAmount * 100), amountMajor: Number(firstPaymentAmount),
        paymentIntentId: paymentIntent.id,
        customerEmail: email, customerName: name, customerPhone: phone,
        productName: formData.course, productId: courseData?.productId || null,
        meta: { source: 'google-apple-pay' }, referralCode: referralCode || '',
        createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
      });

      const nameParts = (name || '').trim().split(' ');
      const accountResult = await createUserAccount(
        { email, firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') || '', phone },
        { amountPaid: Math.round(firstPaymentAmount * 100), productId: courseData?.productId, currency: 'USD', referralCode: referralCode || '', courseKey: formData.course || '', source: 'google-apple-pay', staff: staff || '' }
      );

      await addDoc(collection(db, 'orders'), {
        name: name || '', email, phone,
        staff: staff || '', paymentPlan: 'full', paymentOption: 'google-apple-pay',
        amountPaid: Number(firstPaymentAmount), productName: formData.course,
        productId: courseData?.productId, stripePaymentIntentId: paymentIntent.id,
        status: accountResult.success ? 'completed' : 'failed',
        failureReason: accountResult.success ? '' : accountResult.error || 'Account/order creation failed',
        createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
      });

      setSnackSeverity(accountResult.success ? 'success' : 'error');
      setSnackMessage(accountResult.success
        ? 'Payment successful! Your account has been created.'
        : 'Payment succeeded but account creation failed. Please contact support.');
      setSnackOpen(true);
      sessionStorage.removeItem('paymentUserData');
    } catch (err) {
      setSnackSeverity('error');
      setSnackMessage(err.message || 'An error occurred.');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Kept current every render so the mounted element never confirms against a
  // stale course or amount.
  walletClickRef.current = handleWalletClick;
  walletConfirmRef.current = handleWalletConfirm;

  const handleChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const handleCourseChange = (e) => {
    const selectedCourse = e.target.value;
    const courseData = courseOptions[selectedCourse];
    setFormData((prev) => ({ ...prev, course: selectedCourse }));
    if (courseData?.price != null) setFirstPaymentAmount(courseData.price);
    setSnackSeverity('info');
    setSnackMessage(`Selected course: ${courseDisplayMap[selectedCourse] || selectedCourse}`);
    setSnackOpen(true);
  };

  const validateBeforeSubmit = () => {
    if (!formData.course) { setSnackSeverity('error'); setSnackMessage('Please select a course.'); setSnackOpen(true); return false; }
    if (!acceptedConsent) { setSnackSeverity('error'); setSnackMessage('Please accept the Terms and Conditions and digital content consent to continue.'); setSnackOpen(true); return false; }
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.phone) {
      setSnackSeverity('error'); setSnackMessage('Please complete all required fields.'); setSnackOpen(true); return false;
    }
    return true;
  };

  const openConfirm = () => {
    if (!validateBeforeSubmit()) return;
    if (paymentMethod === 'bank-transfer') { setBankModalOpen(true); return; }
    setConfirmChecked(false); setConfirmTyped(''); setConfirmOpen(true);
  };

  const handleFlexPaySubmit = async (event) => {
    event?.preventDefault?.();
    setLoading(true); setError('');

    if (!acceptedConsent) { setError('Please accept all consents before proceeding.'); setLoading(false); return; }
    if (!stripe || !elements) { setLoading(false); return; }

    const { email, firstName, lastName, phone } = formData;
    const name = `${firstName} ${lastName}`;

    try {
      if (paymentMethod === 'paypal') {
        const currency = 'USD';
        const createPI = await fetch('https://innovators.onrender.com/stripe/create-intent', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: Math.round(Number(firstPaymentAmount) * 100), currency, method: 'paypal', email, name, metadata: { source: 'ups', course: formData.course || '', courseName: selectedCourseDisplay }, returnUrl: `${window.location.origin}/confirmation` }),
        });
        const piData = await createPI.json();
        if (!createPI.ok) throw new Error(piData.error || 'Failed to create PaymentIntent');
        const { clientSecret, paymentIntentId } = piData;
        const courseData = courseOptions[formData.course];
        const pendingPaymentRef = await addDoc(collection(db, 'payments'), {
          status: 'initiated', gateway: 'stripe', method: 'paypal', currency,
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId, customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: 'ups' }, referralCode: referralCode || '',
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        sessionStorage.setItem('paymentUserData', JSON.stringify({ ...formData, firstPaymentAmount, productId: courseData?.productId, course: formData.course, staff: staff || '', paymentPlan: 'part', paymentMethod: 'paypal', paymentIntentId, paymentDocId: pendingPaymentRef.id, referralCode: referralCode || '' }));
        setRedirectOpen(true);
        const { error } = await stripe.confirmPayPalPayment(clientSecret, { return_url: `${window.location.origin}/confirmation`, payment_method: { billing_details: { email, name, phone } } });
        if (error) { setRedirectOpen(false); throw error; }
        setLoading(false); return;
      }

      if (paymentMethod === 'klarna' || paymentMethod === 'clearpay') {
        const currency = 'USD';
        const method = paymentMethod === 'clearpay' ? 'afterpay_clearpay' : 'klarna';
        const createPI = await fetch('https://innovators.onrender.com/stripe/create-intent', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: Math.round(Number(firstPaymentAmount) * 100), currency, method, email, name, metadata: { source: 'ups', course: formData.course || '', courseName: selectedCourseDisplay }, returnUrl: `${window.location.origin}/confirmation` }),
        });
        const piData = await createPI.json();
        if (!createPI.ok) throw new Error(piData.error || 'Failed to create PaymentIntent');
        const { clientSecret, paymentIntentId } = piData;
        const courseData = courseOptions[formData.course];
        const pendingPaymentRef = await addDoc(collection(db, 'payments'), {
          status: 'initiated', gateway: 'stripe', method: paymentMethod, currency,
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId, customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: 'ups' }, referralCode: referralCode || '',
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        sessionStorage.setItem('paymentUserData', JSON.stringify({ ...formData, firstPaymentAmount, productId: courseData?.productId, course: formData.course, staff: staff || '', paymentPlan: 'part', paymentMethod, paymentIntentId, paymentDocId: pendingPaymentRef.id, referralCode: referralCode || '' }));
        setRedirectOpen(true);
        if (paymentMethod === 'klarna') {
          const { error } = await stripe.confirmKlarnaPayment(clientSecret, { return_url: `${window.location.origin}/confirmation`, payment_method: { billing_details: { email, name, phone } } });
          if (error) { setRedirectOpen(false); throw error; }
        } else {
          const { error } = await stripe.confirmAfterpayClearpayPayment(clientSecret, { return_url: `${window.location.origin}/confirmation`, payment_method: { billing_details: { email, name, phone } } });
          if (error) { setRedirectOpen(false); throw error; }
        }
        setLoading(false); return;
      }

      const cardElement = elements.getElement(CardElement);
      const { error: pmErr, paymentMethod: stripePaymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: cardElement, billing_details: { name, email } });
      if (pmErr) { setSnackSeverity('error'); setSnackMessage('Error creating payment method.'); setSnackOpen(true); setLoading(false); return; }

      const endpoint =
        paymentMethod === 'flexpay-us'  ? 'https://innovators.onrender.com/create-customer-usa' :
        paymentMethod === 'flexpay-can' ? 'https://innovators.onrender.com/create-customer-us' : null;
      if (!endpoint) { setSnackSeverity('error'); setSnackMessage('Invalid payment method.'); setSnackOpen(true); setLoading(false); return; }

      const cardResult = await payWithCard({
        stripe, endpoint,
        payload: {
          name, email, phone,
          paymentMethodId: stripePaymentMethod.id,
          firstPaymentAmount,
          course: formData.course || '',
          courseName: selectedCourseDisplay,
          returnUrl: `${window.location.origin}/confirmation`,
        },
      });
      if (cardResult) {
        const courseData = courseOptions[formData.course];
        const currency = paymentMethod === 'flexpay-can' ? 'CAD' : 'USD';
        await addDoc(collection(db, 'payments'), {
          status: 'succeeded', gateway: 'stripe', method: 'card', currency,
          amountMinor: Math.round(Number(firstPaymentAmount) * 100), amountMajor: Number(firstPaymentAmount),
          paymentIntentId: cardResult?.firstPaymentIntent || null, stripeCustomerId: cardResult?.customerId || null,
          customerEmail: email, customerName: name, customerPhone: phone,
          productName: formData.course, productId: courseData?.productId || null,
          meta: { source: 'flexpay' }, referralCode: referralCode || '',
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
        });
        const accountResult = await createUserAccount(
          { email, firstName, lastName, phone },
          { amountPaid: Math.round(Number(firstPaymentAmount) * 100), productId: courseData?.productId, currency, referralCode: referralCode || '', courseKey: formData.course || '', source: paymentMethod, staff: staff || '' }
        );
        await addDoc(collection(db, 'orders'), {
          name, email, phone, staff: staff || '', paymentPlan: 'part',
          paymentOption: paymentMethod, amountPaid: Number(firstPaymentAmount),
          productName: formData.course, productId: courseData?.productId,
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
          status: accountResult.success ? 'completed' : 'failed',
          failureReason: accountResult.success ? '' : accountResult.error || 'Account/order creation failed',
        });
        setSnackMessage(accountResult.success ? 'Payment successful! Account created and access granted.' : 'Payment succeeded, but account creation failed.');
        setSnackSeverity(accountResult.success ? 'success' : 'error');
        setSnackOpen(true);
        sessionStorage.removeItem('paymentUserData');
      } else { setSnackSeverity('error'); setSnackMessage('Payment failed.'); setSnackOpen(true); }
    } catch (err) {
      console.error('Payment error:', err);
      setSnackSeverity('error');
      setSnackMessage(paymentFailureMessage(paymentMethod, err?.code || err?.decline_code, err?.response?.data?.error || err?.message));
      setSnackOpen(true);
    } finally { setLoading(false); }
  };

  return (
    <ExtravagantContainer maxWidth="full">
      <ExtravagantCard>
        <HeaderBox>
          <Typography variant="h3" component="h1">Tritek UnifiedPay</Typography>
          <Typography variant="subtitle1">Experience effortless payments with style.</Typography>
          <PaymentExpiryTimer />
        </HeaderBox>

        <StatusModal open={snackOpen} onClose={() => setSnackOpen(false)} severity={snackSeverity} message={snackMessage} />

        <BankTransferModal
          open={bankModalOpen}
          onClose={() => setBankModalOpen(false)}
          courseName={selectedCourseDisplay}
          amount={selectedCoursePrice}
          currency={activeCurrency}
        />

        <Dialog open={redirectOpen} maxWidth="sm" fullWidth>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Redirecting to {paymentMethodLabel}…</Typography>
                <Typography sx={{ mt: 1, fontWeight: 800 }}>Do NOT close this tab. Do NOT press back.</Typography>
                <Typography sx={{ mt: 0.5 }}>Wait until you return to the final confirmation page.</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: 'rgba(215,109,119,0.12)' }}>
              <Typography sx={{ fontWeight: 900 }}>You are paying for:</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800 }}>{selectedCourseDisplay}</Typography>
              <Typography sx={{ mt: 0.5 }}>Amount: <b>{getCurrencySymbol()}{selectedCoursePrice}</b></Typography>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog open={confirmOpen} onClose={() => !loading && setConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogContent>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Confirm your payment (required)</Typography>
            <Box sx={{ p: 2, borderRadius: 2, border: '2px solid #d76d77', background: 'rgba(255,255,255,0.85)' }}>
              <Typography sx={{ fontWeight: 900 }}>Course:</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800 }}>{selectedCourseDisplay}</Typography>
              <Typography sx={{ mt: 1 }}>Amount: <b>{getCurrencySymbol()}{selectedCoursePrice}</b></Typography>
              <Typography sx={{ mt: 0.5 }}>Payment method: <b>{paymentMethodLabel}</b></Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />}
                label={<Typography sx={{ fontWeight: 800 }}>I confirm this is the exact course I want to pay for.</Typography>}
              />
              <Typography sx={{ mt: 1, fontWeight: 800 }}>Type <b>CONFIRM</b> to continue:</Typography>
              <TextField variant="standard" fullWidth value={confirmTyped} onChange={(e) => setConfirmTyped(e.target.value)} placeholder="Type CONFIRM" />
              {requiresRedirectWait && (
                <Typography sx={{ mt: 2, color: '#8a1f2d', fontWeight: 900 }}>
                  Redirect notice: You MUST wait for the final redirect. Do not close this tab.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
              <Button variant="outlined" fullWidth disabled={loading} onClick={() => setConfirmOpen(false)}>Go back</Button>
              <Button variant="contained" fullWidth
                disabled={loading || !confirmChecked || confirmTyped.trim().toUpperCase() !== 'CONFIRM'}
                onClick={async () => { setConfirmOpen(false); await handleFlexPaySubmit({ preventDefault: () => {} }); }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : `Yes — pay for ${selectedCourseKey}`}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <CardContent sx={{ p: 4 }}>
          <Box component="form" autoComplete="off" display="flex" flexDirection="column" gap={2}>

            <TextField variant="standard" label="Email" fullWidth required disabled={loading} value={formData.email} onChange={handleChange('email')} />
            <TextField variant="standard" label="First Name" fullWidth required disabled={loading} value={formData.firstName} onChange={handleChange('firstName')} />
            <TextField variant="standard" label="Last Name" fullWidth required disabled={loading} value={formData.lastName} onChange={handleChange('lastName')} />
            <TextField variant="standard" label="Phone Number" fullWidth required disabled={loading} value={formData.phone} onChange={handleChange('phone')} />
            <TextField variant="standard" label="Referral Code (optional)" fullWidth disabled={loading}
              value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              helperText="If someone referred you, enter their code here." />

            <FormControl fullWidth required disabled={loading} variant="standard">
              <InputLabel id="course-label" sx={{ fontWeight: 900, fontSize: '1.1rem' }}>Choose Your Required Course</InputLabel>
              <Select labelId="course-label" value={formData.course || ''} onChange={handleCourseChange}>
                {Object.entries(courseOptions).map(([course, data]) => (
                  <MenuItem key={course} value={course}>{courseDisplayMap[course] || course} — {getCurrencySymbol()}{data.price}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 1, p: 2, borderRadius: 2, border: '2px solid rgba(215,109,119,0.35)', background: 'rgba(255,255,255,0.7)' }}>
              <Typography sx={{ fontWeight: 900 }}>You are paying for:</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800 }}>{selectedCourseDisplay}</Typography>
              <Typography sx={{ mt: 0.5 }}>Amount: <b>{getCurrencySymbol()}{selectedCoursePrice}</b></Typography>
              <Typography sx={{ mt: 0.5 }}>Payment method: <b>{paymentMethodLabel}</b></Typography>
              {requiresRedirectWait && (
                <Typography sx={{ mt: 1, color: '#8a1f2d', fontWeight: 800 }}>
                  Important: After confirming, you will be redirected. Do NOT close this page.
                </Typography>
              )}
            </Box>

            {/* ── Merged consent: Terms & Conditions + digital content access waiver ── */}
            <Box sx={{
              p: 2, borderRadius: 2,
              border: `2px solid ${acceptedConsent ? '#2e7d32' : '#c62828'}`,
              background: acceptedConsent ? 'rgba(46,125,50,0.06)' : 'rgba(198,40,40,0.06)',
              transition: 'border-color 0.2s, background 0.2s',
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedConsent}
                    onChange={(e) => setAcceptedConsent(e.target.checked)}
                    name="consent"
                    color="primary"
                    disabled={loading}
                    sx={{ alignSelf: 'flex-start', mt: '2px' }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.6 }}>
                    I agree to the{' '}
                    <a href="https://triteklibrary.co.uk/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>Terms and Conditions</a>
                    {' '}and I expressly request immediate access to the digital content after purchase. I understand that once access begins, I forfeit my 14-day cancellation right under the{' '}
                    <strong>Consumer Rights Act 2015</strong> and{' '}<strong>Consumer Contracts Regulations 2013</strong>.{' '}
                    <Typography component="span" variant="body2" sx={{ color: '#c62828', fontWeight: 800 }}>(Required)</Typography>
                  </Typography>
                }
                alignItems="flex-start"
              />
            </Box>

            {walletMethods && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 1 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', whiteSpace: 'nowrap', px: 1 }}>Quick Pay — Full Payment</Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}

            {/*
              Always rendered: the element needs somewhere to mount before we
              know whether any wallet exists. Collapsed rather than removed,
              and height:0 keeps a real width for Stripe to measure.
            */}
            <Box ref={walletBoxRef} sx={walletMethods ? {} : { height: 0, overflow: 'hidden' }} />

            {walletMethods && (
              <Box>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.75, color: '#888' }}>Google Pay or Apple Pay (full payment, instant access)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', whiteSpace: 'nowrap', px: 1 }}>Or pay another way</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              </Box>
            )}

            <FormControl component="fieldset" variant="standard" disabled={loading}>
              <FormLabel component="legend">Payment plan</FormLabel>
              <RadioGroup row value={paymentPlan}
                onChange={(e) => { setPaymentPlan(e.target.value); setPaymentMethod(e.target.value === 'part' ? 'klarna' : 'flexpay-us'); }}>
                <FormControlLabel value="part" control={<Radio />} label="Part payment" />
                <FormControlLabel value="full" control={<Radio />} label="Full payment" />
              </RadioGroup>
            </FormControl>

            {paymentMethod !== 'klarna' && paymentMethod !== 'clearpay' &&
             paymentMethod !== 'paypal' && paymentMethod !== 'bank-transfer' && (
              <>
                <FormControl fullWidth required variant="standard">
                  <TextField label="First Payment Amount" type="number" required fullWidth variant="standard"
                    value={firstPaymentAmount} onChange={(e) => setFirstPaymentAmount(parseFloat(e.target.value))}
                    error={!!amountError} disabled={true}
                    InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment> }} />
                </FormControl>
                <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', background: '#fafafa' }}>
                  <CardElement options={{ style: { base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } }, invalid: { color: '#9e2146' } } }} />
                </Box>
                <span style={{ color: 'green', fontSize: '13px' }}>Card details are not stored</span>
              </>
            )}

            {paymentPlan === 'part' ? (
              <FormControl fullWidth required disabled={loading}>
                <InputLabel id="payment-method-label">Payment Method</InputLabel>
                <Select labelId="payment-method-label" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} label="Payment Method" variant="standard">
                  <MenuItem value="klarna">Klarna</MenuItem>
                  <MenuItem value="clearpay">ClearPay</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth required disabled={loading}>
                <InputLabel id="payment-method-label">Payment Method</InputLabel>
                <Select labelId="payment-method-label" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} label="Payment Method" variant="standard">
                  <MenuItem value="flexpay-us">Card payment (USD)</MenuItem>
                  <MenuItem value="flexpay-can">Card payment (CAD)</MenuItem>
                  <MenuItem value="bank-transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            )}

            {paymentMethod === 'bank-transfer' && (
              <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(21,101,192,0.07)', border: '1px solid #1565c0' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>
                  Click the button below to view our bank details. Transfer the exact amount and use your full name as the reference.
                </Typography>
              </Box>
            )}

            {error && <Typography color="error" variant="body2">{error}</Typography>}

            {paymentMethod === 'klarna' ? (
              <StyledButton fullWidth disabled={loading} onClick={openConfirm}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay With Klarna'}
              </StyledButton>
            ) : paymentMethod === 'clearpay' ? (
              <StyledButton fullWidth disabled={loading} onClick={openConfirm}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay With ClearPay'}
              </StyledButton>
            ) : paymentMethod === 'paypal' ? (
              <StyledButton fullWidth disabled={loading} onClick={openConfirm}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay With PayPal'}
              </StyledButton>
            ) : paymentMethod === 'bank-transfer' ? (
              <StyledButton fullWidth disabled={loading} onClick={openConfirm}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'View Bank Transfer Details'}
              </StyledButton>
            ) : (
              <StyledButton fullWidth disabled={loading || !!amountError} onClick={openConfirm}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Proceed to FlexPay'}
              </StyledButton>
            )}

            {/* ── Payment method badges ── */}
            <PaymentBadges />

          </Box>
        </CardContent>
      </ExtravagantCard>
    </ExtravagantContainer>
  );
};

const StripeWrapperUs = () => (
  <Elements stripe={stripePromise}>
    <PaymentFormUs />
  </Elements>
);

export default StripeWrapperUs;